const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

const TOKEN_KEY = "spotdeal_admin_access_token";
const REFRESH_KEY = "spotdeal_admin_refresh_token";
const USER_KEY = "spotdeal_admin_user";

export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_KEY),
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_KEY, refreshToken);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

export function parseErrorMessage(body, status) {
  return body?.error?.message || body?.message || `Request failed (${status})`;
}

function isAuthFailure(status) {
  return status === 401 || status === 403;
}

async function refreshAccessToken() {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/admin/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false || !body.data?.accessToken) {
      return null;
    }

    tokenStorage.setTokens(body.data.accessToken, body.data.refreshToken);
    if (body.data.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(body.data.user));
    }
    return body.data.accessToken;
  } catch {
    return null;
  }
}

export async function apiRequest(path, options = {}) {
  const { _retried, ...fetchOptions } = options;
  const headers = {
    "Content-Type": "application/json",
    ...(fetchOptions.headers || {}),
  };

  const token = tokenStorage.getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...fetchOptions,
      headers,
    });
  } catch {
    throw new Error("Network error — check API URL and connection");
  }

  if (isAuthFailure(response.status) && tokenStorage.getRefreshToken() && !_retried) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return apiRequest(path, { ...options, _retried: true });
    }
  }

  const body = await response.json().catch(() => ({}));

  if (!response.ok || body.success === false) {
    const err = new Error(parseErrorMessage(body, response.status));
    err.status = response.status;
    throw err;
  }

  return body;
}

export default apiRequest;
